import type { Order, TrackingEvent, YunExpressTrackEvent } from './types';
import { DELIVERED_STATUS, PARXL_DELIVERED_STATUS, YUN_EXPRESS_DELIVERED_STATUS, OrderStatus } from '../../../constants/order';
import { formatLongDate, formatDateTime } from '../../../utils/date';

/**
 * Parses Deleo eventTime string (KST) to Date
 * API format: "2025-12-18 09:08:00" in KST (UTC+9)
 */
const parseDeleoEventTime = (eventTime: string): Date =>
  new Date(eventTime.replace(' ', 'T') + '+09:00');

/**
 * Sorts YunExpress track events by processUtcTime descending (newest first)
 */
const sortYunExpressEventsByTimeDesc = (events: YunExpressTrackEvent[]): YunExpressTrackEvent[] =>
  [...events].sort((a, b) =>
    new Date(b.processUtcTime).getTime() - new Date(a.processUtcTime).getTime()
  );

/**
 * Checks if the order is a PARXL order
 */
const isParxlOrder = (order: Order): boolean => {
  const provider = order.shippingData?.provider;
  return provider === 'PARXL' || (!provider && !!order.parxlData);
};

/**
 * Checks if the order is a YunExpress order
 */
const isYunExpressOrder = (order: Order): boolean => {
  const provider = order.shippingData?.provider;
  return provider === 'YUN_EXPRESS' || (!provider && !!order.yunExpressData);
};

/**
 * Transforms Deleo trace events to UI TrackingEvent format
 * API returns timestamps in KST (Korea Standard Time, UTC+9)
 */
const transformDeleoTrackingEvents = (order: Order): TrackingEvent[] => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;

  if (!traceList || traceList.length === 0) {
    return [];
  }

  return traceList.map((event, index) => {
    const isFirstEvent = index === 0;
    const isDelivered = event.status === DELIVERED_STATUS;
    const isCurrentEvent = isFirstEvent && !isDelivered;

    const eventStatus: TrackingEvent['status'] = isCurrentEvent ? 'current' : 'completed';

    const eventDate = parseDeleoEventTime(event.eventTime);
    const formattedTimestamp = formatDateTime(eventDate);

    return {
      id: `${index}-${event.eventTime}`,
      type: event.status,
      title: event.description,
      timestamp: formattedTimestamp,
      status: eventStatus,
    };
  });
};

/**
 * Transforms PARXL tracking events to UI TrackingEvent format
 * API returns timestamps in ISO 8601 format (UTC)
 */
const transformParxlTrackingEvents = (order: Order): TrackingEvent[] => {
  const trackingStatus = order.parxlData?.trackingStatus;

  if (!trackingStatus || trackingStatus.length === 0) {
    return [];
  }

  return trackingStatus.map((event, index) => {
    const isFirstEvent = index === 0;
    const isDelivered = event.status === PARXL_DELIVERED_STATUS;
    const isCurrentEvent = isFirstEvent && !isDelivered;

    const eventStatus: TrackingEvent['status'] = isCurrentEvent ? 'current' : 'completed';

    // API format: ISO 8601 "2025-12-29T22:18:01.706Z"
    const eventDate = new Date(event.date);
    const formattedTimestamp = formatDateTime(eventDate);

    return {
      id: `${index}-${event.date}`,
      type: event.status,
      title: event.status,
      timestamp: formattedTimestamp,
      description: event.location,
      status: eventStatus,
    };
  });
};

/**
 * Transforms YunExpress track events to UI TrackingEvent format
 * API returns processUtcTime in UTC format
 */
const transformYunExpressTrackingEvents = (order: Order): TrackingEvent[] => {
  const trackEvents = order.yunExpressData?.trackInfo?.trackEvents;

  if (!trackEvents || trackEvents.length === 0) {
    return [];
  }

  return sortYunExpressEventsByTimeDesc(trackEvents).map((event, index) => {
    const isFirstEvent = index === 0;
    const isDelivered = event.trackNodeCode === YUN_EXPRESS_DELIVERED_STATUS;
    const isCurrentEvent = isFirstEvent && !isDelivered;

    const eventStatus: TrackingEvent['status'] = isCurrentEvent ? 'current' : 'completed';

    const eventDate = new Date(event.processUtcTime);
    const formattedTimestamp = formatDateTime(eventDate);

    return {
      id: `${index}-${event.processUtcTime}`,
      type: event.trackNodeCode as TrackingEvent['type'],
      title: event.processContent,
      timestamp: formattedTimestamp,
      description: [event.processCity, event.processCountry].filter(Boolean).join(', ') || undefined,
      status: eventStatus,
    };
  });
};

/**
 * Transforms tracking events to UI TrackingEvent format
 * Supports Deleo, PARXL, and YunExpress providers
 */
export const transformTrackingEvents = (order: Order): TrackingEvent[] => {
  if (isYunExpressOrder(order)) {
    return transformYunExpressTrackingEvents(order);
  }

  if (isParxlOrder(order)) {
    return transformParxlTrackingEvents(order);
  }

  return transformDeleoTrackingEvents(order);
};

/**
 * Gets the tracking number from the order
 * Returns the tracking number that matches the carrier:
 * - YunExpress: Returns trackInfo.trackingNumber, fallback to waybillNumber, then shippingData.trackingNumber
 * - PARXL: Returns PARXL tracking number
 * - Deleo with local carrier (FedEx, DHL, etc.): Returns local carrier tracking number
 * - Fallback: Returns Deleo tracking number
 */
export const getTrackingNumber = (order: Order): string => {
  // YunExpress - use trackInfo.trackingNumber, waybillNumber, or shippingData.trackingNumber
  if (isYunExpressOrder(order)) {
    return order.yunExpressData?.trackInfo?.trackingNumber
      || order.yunExpressData?.waybillNumber
      || order.shippingData?.trackingNumber
      || '';
  }

  // PARXL - use parxlData.plsShipmentReference or shippingData.trackingNumber
  if (isParxlOrder(order)) {
    return order.parxlData?.plsShipmentReference || order.shippingData?.trackingNumber || '';
  }

  // Deleo with local carrier - use local carrier tracking number
  const localTrackingNumber = order.deleoData?.orderTrackingList?.[0]?.trackingNo;
  if (localTrackingNumber) {
    return localTrackingNumber;
  }

  // Fallback to Deleo tracking number
  if (order.deleoData?.deleoTrackingNo) {
    return order.deleoData.deleoTrackingNo;
  }

  // Final fallback to shippingData.trackingNumber
  if (order.shippingData?.trackingNumber) {
    return order.shippingData.trackingNumber;
  }

  return '';
};

/**
 * Gets the delivery date from Deleo tracking events
 * Returns the date of the most recent tracking event
 */
const getDeleoDeliveryDate = (order: Order): string | undefined => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;
  const latestEvent = traceList?.[0];

  if (latestEvent?.eventTime) {
    const date = parseDeleoEventTime(latestEvent.eventTime);
    return formatLongDate(date);
  }

  return undefined;
};

/**
 * Gets the delivery date from PARXL tracking events
 * Returns the date of the most recent tracking event
 */
const getParxlDeliveryDate = (order: Order): string | undefined => {
  const trackingStatus = order.parxlData?.trackingStatus;
  const latestEvent = trackingStatus?.[0];

  if (latestEvent?.date) {
    const date = new Date(latestEvent.date);
    return formatLongDate(date);
  }

  return undefined;
};

/**
 * Gets the delivery date from YunExpress tracking events
 * Returns the date of the most recent tracking event
 */
const getYunExpressDeliveryDate = (order: Order): string | undefined => {
  const trackEvents = order.yunExpressData?.trackInfo?.trackEvents;

  if (!trackEvents || trackEvents.length === 0) {
    return undefined;
  }

  const latestEvent = sortYunExpressEventsByTimeDesc(trackEvents)[0];

  if (latestEvent?.processUtcTime) {
    const date = new Date(latestEvent.processUtcTime);
    return formatLongDate(date);
  }

  return undefined;
};

/**
 * Gets the delivery date from the order tracking events
 * Only shows delivery date when order status is 'completed'
 */
export const getDeliveryDate = (order: Order): string | undefined => {
  // Only show delivery date when order is completed
  if (order.status !== OrderStatus.Completed) {
    return undefined;
  }

  if (isYunExpressOrder(order)) {
    return getYunExpressDeliveryDate(order);
  }

  if (isParxlOrder(order)) {
    return getParxlDeliveryDate(order);
  }

  return getDeleoDeliveryDate(order);
};

/**
 * Gets the carrier name from the order
 * - YunExpress: Returns "YunExpress"
 * - PARXL: Returns "PARXL"
 * - Deleo: Returns local carrier name from orderTrackingList (e.g., "FedEx", "DHL"), fallback to "DELEO"
 * - Returns null if no carrier data available
 */
export const getCarrier = (order: Order): string | null => {
  if (isYunExpressOrder(order)) {
    return 'YunExpress';
  }

  if (isParxlOrder(order)) {
    return 'PARXL';
  }

  // Deleo - get local carrier from orderTrackingList, fallback to "DELEO"
  if (order.deleoData) {
    const carrierName = order.deleoData.orderTrackingList?.[0]?.deliveryCompanyCd?.name;
    return carrierName ?? 'DELEO';
  }

  return null;
};

/**
 * Gets the order date formatted
 * orderDateTime is in GMT without timezone suffix, so we add 'Z' to indicate UTC
 */
export const getFormattedOrderDate = (order: Order): string => {
  // API format: "2025-12-11T13:08:19" in GMT (UTC+0)
  // Add 'Z' suffix to ensure correct interpretation as UTC
  const date = new Date(`${order.orderDateTime}Z`);
  return formatLongDate(date);
};

/**
 * Generates the receipt URL for an order
 */
export const getReceiptUrl = (order: Order): string => {
  return `/my-account/view-order/${order.orderId}/`;
};

/**
 * Calculates the item price from line item data
 * Uses direct price if available, otherwise calculates from total and quantity
 */
const calculateItemPrice = (item: Order['lineItems'][0]): number => {
  if (item.price !== undefined) {
    return item.price;
  }

  if (item.total !== undefined && item.quantity > 0) {
    return item.total / item.quantity;
  }

  return 0;
};

interface TransformOrderOptions {
  isRegistered?: boolean;
}

/**
 * Transforms Order API response to display format for OrderCard
 * @param order - The order data from API
 * @param options - Transform options
 * @param options.isRegistered - If true, includes receiptUrl (for logged-in users only)
 */
export const transformOrderForDisplay = (order: Order, options: TransformOrderOptions = {}) => {
  const { isRegistered = false } = options;

  return {
    id: Number(order.orderId),
    orderNumber: `#${order.orderId}`,
    status: order.status,
    orderDate: getFormattedOrderDate(order),
    deliveryDate: getDeliveryDate(order),
    trackingNumber: getTrackingNumber(order),
    carrier: getCarrier(order),
    total: order.totalAmount,
    currency: order.currency,
    items: order.lineItems.map((item, index) => ({
      id: index,
      productId: item.productId ?? item.parentProductId,
      name: item.name,
      imageUrl: item.wooCommerceImageUrl,
      variant: [item.productSize, item.productPack].filter(Boolean).join(' '),
      quantity: item.quantity,
      price: calculateItemPrice(item),
      brand: item.brand,
      category: item.category,
    })),
    trackingEvents: transformTrackingEvents(order),
    receiptUrl: isRegistered ? getReceiptUrl(order) : undefined,
  };
};
